import type {
  Actuator,
  Body,
  BodyUUID,
  Equality,
  HingeJoint,
  Joint,
  JointKind,
  JointUUID,
  SlideJoint,
  Vector3,
} from "../../types/scene";
import { generateUuid } from "../../utils/id";
import { logger } from "../../utils/logger";
import type { JointInput } from "./actionTypes";

// Type guards for joint types
export const isScalarJoint = (joint: Joint): joint is HingeJoint | SlideJoint => {
  return joint.kind === "hinge" || joint.kind === "slide";
};

const AUTO_JOINT_NAME_PATTERN = /^joint_(hinge|slide|ball|free)(?:_\d+)?$/;

export const getDefaultJointName = (kind: JointKind): string => `joint_${kind}`;

export const shouldAutoRenameJointName = (name: unknown): boolean => {
  return typeof name === "string" && AUTO_JOINT_NAME_PATTERN.test(name.trim());
};

// MuJoCo requires hinge/slide joints to have a non-zero axis vector.
// Returns null when the axis cannot be recovered so callers can reject the update.
export const sanitizeJointAxis = (axis: Vector3 | undefined | null): Vector3 | null => {
  if (!axis) return null;
  const x = Number(axis[0]);
  const y = Number(axis[1]);
  const z = Number(axis[2]);
  if (![x, y, z].every(Number.isFinite)) return null;
  if (Math.hypot(x, y, z) < 1e-8) return null;
  return [x, y, z];
};

// MuJoCo rejects negative damping/frictionloss/stiffness on joints.
export const sanitizeJointNonNegative = (value: number, fallback = 0): number => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, n);
};

// Sanitize a joint position vector (used for anchors on hinge/slide/ball joints).
export const sanitizeJointPosition = (position: Vector3 | undefined | null): Vector3 => {
  const out: Vector3 = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    const n = Number(position?.[i]);
    out[i] = Number.isFinite(n) ? n : 0;
  }
  return out;
};

export type JointMap = Record<string, Joint>;
export type JointEquality = Equality & { name1?: string; name2?: string };
export type LegacyJointRef = JointUUID | { uuid?: unknown; name?: unknown };
type EmbeddableBody = { name?: string; joints?: LegacyJointRef[] };
export type BodyWithLegacyJointRefs = Omit<Body, "joints"> & { joints?: LegacyJointRef[] };
export type BodyMapWithLegacyJointRefs = Record<string, BodyWithLegacyJointRefs>;
type EmbeddableBodies = Record<string, EmbeddableBody> | EmbeddableBody[];
export type JointRecordByString = Record<string, Joint>;
export type ActuatorRecordByString = Record<string, Actuator>;
export type EqualityRecordByString = Record<string, Equality>;
export type ScalarJointInput = JointInput & {
  kind: "hinge" | "slide";
  axis?: Vector3;
  position?: Vector3;
  range?: [number, number];
};
export type JointKindUpdate = Partial<Joint> & {
  uuid: JointUUID;
  name: string;
  kind: JointKind;
  position?: Vector3;
  axis?: Vector3;
  range?: [number, number];
  useLimits?: "auto" | "true" | "false";
  max_rotation?: number;
};

export type JointInitInput = Joint | JointInput;

/**
 * UUID-only lookup helper for joint maps.
 */
export const findJointInMap = (jointMap: JointMap, jointIdentifier: string): Joint | null => {
  if (!jointMap || !jointIdentifier) return null;
  return jointMap[jointIdentifier] || null;
};

/**
 * Validates and cleans up body.joints to ensure they're all valid UUIDs.
 * Works directly with flat bodies structure - no tree traversal needed.
 *
 * @param bodiesMap - Flat map of bodies by UUID
 * @param jointMap - Map of joints by UUID
 */
export const embedJointsIntoBodies = (bodiesMap: EmbeddableBodies, jointMap: JointMap = {}) => {
  if (!bodiesMap || typeof bodiesMap !== "object") return;
  const bodies = Array.isArray(bodiesMap) ? bodiesMap : Object.values(bodiesMap);

  // Validate and clean up body.joints to ensure they're all valid UUIDs
  bodies.forEach((body) => {
    if (!body) return;
    if (Array.isArray(body.joints)) {
      // Filter out invalid UUIDs and ensure all entries are valid JointUUIDs
      body.joints = body.joints.filter((uuid): uuid is JointUUID => {
        if (typeof uuid !== "string") {
          logger.error(
            `[embedJointsIntoBodies] Invalid entry in body "${body.name}".joints: expected JointUUID (string), got ${typeof uuid}`
          );
          return false;
        }
        if (!jointMap[uuid]) {
          logger.error(
            `[embedJointsIntoBodies] Joint UUID "${uuid}" in body "${body.name}" not found in jointMap.`
          );
          return false;
        }
        return true;
      }) as JointUUID[];
    }
  });
};

export const createJointRecord = (joint: JointInitInput): { uuid: JointUUID; joint: Joint } => {
  const uuid = (joint.uuid ?? generateUuid()) as JointUUID;
  const jointCandidate = joint as Joint;
  const scalarRef =
    isScalarJoint(jointCandidate) && Number.isFinite(jointCandidate.ref)
      ? Number(jointCandidate.ref)
      : null;
  const normalized: Joint = {
    ...(joint as Joint),
    ...(scalarRef !== null &&
    isScalarJoint(jointCandidate) &&
    !Number.isFinite(jointCandidate.initialRef)
      ? { initialRef: scalarRef }
      : {}),
    uuid: uuid,
  };
  return { uuid, joint: normalized };
};

export const buildJointMap = (
  input: JointInitInput[] | Record<string, JointInitInput> = []
): JointMap => {
  const entries = Array.isArray(input) ? input : Object.values(input || {});
  return entries.reduce((acc, entry) => {
    if (!entry) return acc;
    const { uuid, joint } = createJointRecord(entry);
    acc[uuid] = joint;
    return acc;
  }, {} as JointMap);
};

export const updateJointMapEntry = (
  jointMap: JointMap | undefined,
  jointUuid: string,
  updates: Partial<Joint>
) => {
  if (!jointMap) return;
  const existing = jointMap[jointUuid];
  if (!existing) return;
  jointMap[jointUuid] = { ...existing, ...updates } as Joint;
};

export const findHostBodyUuidForJoint = (
  bodiesMap: BodyMapWithLegacyJointRefs | undefined,
  jointUuid: string
): BodyUUID | null => {
  if (!bodiesMap || !jointUuid) return null;
  for (const [bodyUuid, body] of Object.entries(bodiesMap)) {
    const refs = Array.isArray(body?.joints) ? body.joints : [];
    if (
      refs.some((entry) =>
        typeof entry === "string" ? entry === jointUuid : entry?.uuid === jointUuid
      )
    ) {
      return bodyUuid as BodyUUID;
    }
  }
  return null;
};

export const findParentBodyUuid = (
  bodiesMap: BodyMapWithLegacyJointRefs | undefined,
  childUuid: string
): BodyUUID | null => {
  if (!bodiesMap || !childUuid) return null;
  for (const [bodyUuid, body] of Object.entries(bodiesMap)) {
    const children = Array.isArray(body?.children) ? body.children : [];
    if (children.some((child) => child === childUuid)) return bodyUuid as BodyUUID;
  }
  return null;
};

export const hasOtherFreeJointOnBody = (
  draft: { bodies?: BodyMapWithLegacyJointRefs; joints?: JointMap },
  bodyUuid: string,
  excludeJointUuid?: string
): boolean => {
  const body = draft?.bodies?.[bodyUuid];
  if (!body) return false;
  const refs = Array.isArray(body.joints) ? body.joints : [];
  return refs.some((ref) => {
    const refUuid = typeof ref === "string" ? ref : ref?.uuid;
    if (typeof refUuid !== "string") return false;
    if (!refUuid || (excludeJointUuid && refUuid === excludeJointUuid)) return false;
    const candidate = draft?.joints?.[refUuid];
    return candidate?.kind === "free";
  });
};

export const getJointKindRefsOnBody = (
  jointsMap: Record<string, Joint> | undefined,
  bodiesMap: BodyMapWithLegacyJointRefs | undefined,
  bodyUuid: string,
  excludeJointUuid?: string
): Array<{ uuid: string; kind: JointKind | undefined }> => {
  const body = bodiesMap?.[bodyUuid];
  if (!body) return [];
  const refs = Array.isArray(body.joints) ? body.joints : [];
  return refs
    .map((ref) => (typeof ref === "string" ? ref : ref?.uuid))
    .filter((refUuid): refUuid is string => {
      return !!refUuid && (!excludeJointUuid || refUuid !== excludeJointUuid);
    })
    .map((refUuid: string) => ({
      uuid: refUuid,
      kind: jointsMap?.[refUuid]?.kind,
    }));
};

export const hasBallRotationalConflict = (
  jointsMap: Record<string, Joint> | undefined,
  bodiesMap: BodyMapWithLegacyJointRefs | undefined,
  bodyUuid: string,
  incomingKind: JointKind | undefined,
  excludeJointUuid?: string
): boolean => {
  if (incomingKind !== "ball" && incomingKind !== "hinge") return false;
  const siblingKinds = getJointKindRefsOnBody(jointsMap, bodiesMap, bodyUuid, excludeJointUuid);
  if (incomingKind === "ball") {
    return siblingKinds.some(({ kind }) => kind === "ball" || kind === "hinge");
  }
  return siblingKinds.some(({ kind }) => kind === "ball");
};

export const warnBallRotationalConflict = (
  actionName: string,
  incomingKind: JointKind | undefined
) => {
  const subject =
    incomingKind === "ball"
      ? "ball joints cannot share a body with hinge or ball joints"
      : "hinge joints cannot share a body with a ball joint";
  logger.warn(`[jointActions] Rejected ${actionName}: ${subject}.`);
};
